import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@attendance/ui/utils';
import { SEARCH_MIN_QUERY_LENGTH } from '@attendance/shared/searchLimits';
import type { GlobalSearch } from './useGlobalSearch.js';
import { ICON } from '@attendance/ui';

/**
 * The global search's results as an ARIA listbox — the same list under the app
 * bar's field and inside the ⌘K palette. Focus stays in the text box; the
 * active row is announced through `aria-activedescendant` (see `optionId`).
 */
export function SearchResults({
  search,
  listId,
  optionId,
  activeIndex,
  setActive,
  onPick,
  listRef,
  className,
}: {
  search: GlobalSearch;
  listId: string;
  optionId: (index: number) => string;
  activeIndex: number;
  setActive: (index: number) => void;
  onPick: (to: string) => void;
  listRef?: RefObject<HTMLUListElement>;
  className?: string;
}): JSX.Element {
  const { t } = useTranslation();
  let rowIndex = -1;
  return (
    <ul
      ref={listRef}
      id={listId}
      role="listbox"
      aria-label={t('command.searchTitle', 'Search')}
      className={cn('max-h-80 overflow-y-auto py-1', className)}
    >
      {search.sections.map((section) => (
        <li key={section.id} role="presentation">
          {/* `text-eyebrow`, not `text-xs uppercase tracking-wide`. This is
            * a group heading in a list — the eyebrow role — and the system
            * has exactly ONE uppercase step for that (§1: 12px/600/+0.3px).
            * The old spelling was off-scale (`text-xs` is a raw Tailwind
            * step, not a token) and tracked at +0.05em, which is a fourth
            * uppercase treatment nobody chose. `text-eyebrow` carries the
            * uppercase itself, so the utility does not restate it. */}
          <p className="px-4 pb-1 pt-2 text-eyebrow text-muted-foreground">
            {section.title}
          </p>
          <ul role="group" aria-label={section.title}>
            {section.rows.map((row) => {
              rowIndex += 1;
              const index = rowIndex;
              const isActive = index === activeIndex;
              return (
                <li key={row.key} role="presentation">
                  <button
                    type="button"
                    id={optionId(index)}
                    role="option"
                    aria-selected={isActive}
                    tabIndex={-1}
                    onMouseEnter={() => setActive(index)}
                    // Keep focus in the field: a click must not blur it shut first.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onPick(row.to)}
                    className={cn(
                      'flex w-full items-center gap-inline px-4 py-2.5 text-start text-copy transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted'
                    )}
                  >
                    <row.icon size={ICON.control} className="shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{row.label}</span>
                      {row.sublabel && (
                        <span
                          className={cn(
                            'block truncate text-copy-sm',
                            isActive ? 'text-primary/70' : 'text-muted-foreground'
                          )}
                        >
                          {row.sublabel}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </li>
      ))}

      {/* ERROR — deliberately its own message, and never mistakable for
          "nothing matched". The pages above keep working. */}
      {search.dataFailed && (
        <li
          role="presentation"
          className="flex items-start gap-element px-cell-x py-cell-y text-copy text-danger-on-surface"
        >
          <AlertTriangle size={ICON.control} className="mt-0.5 shrink-0" aria-hidden />
          <span role="alert">
            {t(
              'command.searchFailed',
              'Search failed. This is not the same as no matches.'
            )}
          </span>
        </li>
      )}

      {search.searching && search.flat.length === 0 && (
        <li
          role="presentation"
          className="px-4 py-6 text-center text-copy text-muted-foreground"
          aria-live="polite"
        >
          {t('command.searching', 'Searching…')}
        </li>
      )}

      {search.typedTooShort && (
        <li role="presentation" className="px-4 py-6 text-center text-copy text-muted-foreground">
          {t('command.minLength', 'Type at least {{count}} characters to search records', {
            count: SEARCH_MIN_QUERY_LENGTH,
          })}
        </li>
      )}

      {search.showEmpty && (
        <li role="presentation" className="px-4 py-6 text-center text-copy text-muted-foreground">
          {t('command.noMatches', 'No matching pages or records')}
        </li>
      )}

      {search.truncated && (
        <li
          role="presentation"
          className="border-t border-border px-4 py-2 text-copy-sm text-muted-foreground"
        >
          {t('command.truncated', 'Showing the top matches')}
        </li>
      )}
    </ul>
  );
}
